import { purchasedTier } from '@src/components/PremiumBadge/tierIdentity';
import '@src/components/PremiumBadge/PremiumTierIdentity.css';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { SessionObject } from '@lub0v/santa-utils';
import { AccountChip } from '@src/components/AccountChip/AccountChip';
import {
  AccountSwitcher,
  type SwitcherBusyAction,
} from '@src/components/AccountSwitcher/AccountSwitcher';
import { getProfileName, profileHomePath } from '@src/components/AccountSwitcher/profiles';
import { UserIcon } from '@src/components/icons/UserIcon';
import { showDialog } from '@src/components/Dialog/Dialog';

import { ADMIN_PATH, ACCOUNT_BOXES_PATH, ACCOUNT_PATH, LOGIN_PATH } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppDispatch } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';
import './AccountSwitcherHost.css';

export interface AccountSwitcherHostProps {
  session: SessionObject;
  /** Expand within the hamburger drawer instead of floating beside the toolbar. */
  inDrawer?: boolean;
  fullPage?: boolean;
}

/** Header trigger plus the profile panel. Same wallet actions as v1. */
export function AccountSwitcherHost({
  session,
  inDrawer = false,
  fullPage = false,
}: AccountSwitcherHostProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [opened, setOpened] = useState(false);
  const [busyProfileId, setBusyProfileId] = useState<number | undefined>();
  const [busyAction, setBusyAction] = useState<SwitcherBusyAction | undefined>();
  const [error, setError] = useState<string | undefined>();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!opened || fullPage) {
      return undefined;
    }
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpened(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpened(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [opened, fullPage]);

  const profiles = session.profiles || [];
  const activeProfile = profiles.find((profile) => profile.id === session.id || profile.active);
  const activeName = activeProfile ? getProfileName(activeProfile, t('switcher.profile')) : '';
  const labels = {
    title: t('switcher.title'),
    profile: t('switcher.profile'),
    guest: t('switcher.guest'),
    active: t('switcher.active'),
    organizer: t('switcher.organizer'),
    noBoxes: t('switcher.noBoxes'),
    moreOne: t('switcher.moreOne'),
    more: (count: number) => t('switcher.more', { count }),
    goToAccount: t('switcher.goToAccount'),
    signOut: t('switcher.signOut'),
    addAccount: t('switcher.addAccount'),
    signOutAll: t('switcher.signOutAll'),
    linkGuest: t('switcher.linkGuest'),
    switchTo: (name: string) => t('switcher.switchTo', { name }),
  };

  function showError() {
    setError(t('switcher.actionError'));
  }

  function toggle() {
    if (!profiles.length) {
      navigate(ACCOUNT_PATH);
      return;
    }
    setError(undefined);
    setOpened(!opened);
  }

  function goToAccount() {
    setOpened(false);
    if (location.pathname === ACCOUNT_PATH) {
      return;
    }
    navigate(ACCOUNT_PATH);
  }

  async function activateProfile(userId: number) {
    if (busyProfileId !== undefined || busyAction) {
      return;
    }
    setError(undefined);
    setBusyProfileId(userId);
    try {
      const result = await santaClient.activateProfile({ userId });
      if (result.ok) {
        const activated =
          result.data?.profiles?.find((profile) => profile.id === userId || profile.active) ||
          profiles.find((profile) => profile.id === userId);
        window.location.assign(profileHomePath(activated));
        return;
      }
    } catch {
      // One generic error — never leak request details.
    }
    setBusyProfileId(undefined);
    showError();
  }

  function selectProfile(userId: number) {
    if (userId === session.id || userId === activeProfile?.id) {
      goToAccount();
      return;
    }
    void activateProfile(userId);
  }

  async function performLinkGuest(userId: number) {
    setError(undefined);
    setBusyAction('link');
    setBusyProfileId(userId);
    try {
      const res = await santaClient.linkGuestProfile({ userId });
      if (res.ok) {
        await dispatch(loadSession());
        setBusyAction(undefined);
        setBusyProfileId(undefined);
        setOpened(false);
        window.location.assign(ACCOUNT_BOXES_PATH);
        return;
      }
    } catch {
      // One generic error — never leak request details.
    }
    setBusyAction(undefined);
    setBusyProfileId(undefined);
    setOpened(true);
    showError();
  }

  function linkGuest(userId: number) {
    if (busyProfileId !== undefined || busyAction) {
      return;
    }
    const guest = profiles.find((profile) => profile.id === userId);
    if (!guest?.isGuest || activeProfile?.isGuest) {
      return;
    }
    setOpened(false);
    showDialog({
      title: t('switcher.linkGuestTitle'),
      children: t('switcher.linkGuestMessage'),
      cancel: t('box.cancel'),
      submit: t('switcher.linkGuestConfirm'),
      onSubmit: () => performLinkGuest(userId),
      testId: 'switcher-link-guest',
    });
  }

  function addAccount() {
    setOpened(false);
    setError(undefined);
    navigate(LOGIN_PATH);
  }

  async function signOut() {
    if (busyProfileId !== undefined || busyAction) {
      return;
    }
    setError(undefined);
    setBusyAction('sign-out');
    try {
      const result = await santaClient.removeProfile({ userId: session.id });
      if (result.ok) {
        window.localStorage.removeItem('refresh');
        if (result.data?.profiles?.length) {
          const next =
            result.data.profiles.find((profile) => profile.active) || result.data.profiles[0];
          window.location.assign(profileHomePath(next));
        } else {
          window.location.assign('/');
        }
        return;
      }
    } catch {
      // One generic error — never leak request details.
    }
    setBusyAction(undefined);
    showError();
  }

  async function performSignOutAll() {
    setError(undefined);
    setBusyAction('sign-out-all');
    try {
      const result = await santaClient.removeAllProfiles();
      if (result.ok) {
        window.localStorage.removeItem('refresh');
        window.location.assign('/');
        return;
      }
    } catch {
      // One generic error — never leak request details.
    }
    setBusyAction(undefined);
    setOpened(true);
    showError();
  }

  function signOutAll() {
    if (busyProfileId !== undefined || busyAction) {
      return;
    }
    setOpened(false);
    showDialog({
      title: t('switcher.signOutAllTitle'),
      children: t('switcher.signOutAllMessage'),
      cancel: t('box.cancel'),
      submit: t('switcher.signOutAllConfirm'),
      submitVariant: 'danger',
      onSubmit: performSignOutAll,
      testId: 'switcher-sign-out-all-dialog',
    });
  }

  return (
    <div
      className={`account-switcher-host header-account-switcher${inDrawer || fullPage ? ' account-switcher-host--drawer' : ''}`}
      ref={rootRef}
    >
      {!fullPage && (
        <button
          type="button"
          className="account-switcher-host__trigger header-account-switcher__trigger"
          data-premium-tier={purchasedTier(session)}
          aria-expanded={opened}
          aria-haspopup="dialog"
          aria-label={t('switcher.title')}
          onClick={toggle}
        >
          <AccountChip
            icon={<UserIcon />}
            labelHidden={!inDrawer}
            label={activeName || t('header.account')}
            testId={inDrawer ? 'header-profiles-menu' : 'header-account'}
          >
            {inDrawer ? t('switcher.title') : null}
          </AccountChip>
        </button>
      )}
      {opened || fullPage ? (
        <AccountSwitcher
          embedded={fullPage}
          profiles={profiles}
          activeUserId={session.id}
          labels={labels}
          busyProfileId={busyProfileId}
          busyAction={busyAction}
          error={error}
          onSelectProfile={selectProfile}
          onLinkGuest={activeProfile?.isGuest ? undefined : linkGuest}
          onGoToAccount={goToAccount}
          onGoToAdmin={!session.isGuest && (session.accountRole === 'super_admin' || /^goods_admin_.+/.test(session.accountRole || '')) ? () => {
            setOpened(false);
            navigate(ADMIN_PATH);
          } : undefined}
          onAddAccount={addAccount}
          onSignOut={signOut}
          onSignOutAll={signOutAll}
        />
      ) : null}
    </div>
  );
}
